import Image from 'next/image'
import { useContext, useState, useEffect, useRef } from 'react'
import {
  Burger,
  BurgerLine,
  Icons,
  MenuContainer,
  MenuItemText,
  MenuItemLink,
  SearchContainer,
  SearchTrigger,
  UserInitials,
  Wrapper,
  DesktopLogo,
  MobileLogo,
  BodySpacer,
  TopbarContainer,
  MenuItemWrapper,
  MenuLink,
  ImageWrapper,
  VelocityIcon,
  Points,
  PointValue,
  UserName,
  Email,
  Reservations,
  DropdownUserWrapper,
  PointsWrapper,
} from './VirginTopbar.styles'
import { CartIcon, Total } from './Topbar.styles'
import Icon from './Icons'
import OffCanvasMenu from './OffCanvasMenu'
import { signIn, signOut } from 'next-auth/react'
import { useAuthSession } from '@/hooks/AuthSessionHook'
import Link from 'next/link'
import { useRouter } from 'next/router'
import SearchModal from './SearchModal'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import Dropdown from './Dropdown'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { useAnalyticsUserAction } from '@/hooks/AnalyticsUserHook'
import { CACHE_EXPIRY_DATE_KEY_NAME } from '@/constant/segment'
import { useAnalyticsSearchModalInteraction } from '@/hooks/AnalyticsSearchModalInteractionHook'
import { ModalTypes } from '@/types'
import dynamic from 'next/dynamic'
import { Button } from '@big-red-group/storefront-common-ui'
import { useCart } from '@big-red-group/storefront-common-checkout'
const HappyHour = dynamic(() => import('./HappyHour'), { ssr: false })
import ProductTile from './ProductTile'
import { SearchPopup } from '@big-red-group/storefront-common-ui'

const VirginTopbar = () => {
  const placement = 'VirginTopbar'
  const searchType: ModalTypes = 'search'

  const defaultPropertiesContext = useDefaultProperties()
  const baseUrl = getUrlPrefix('virgin')
  const { data: cart, mutate } = useCart('cart', baseUrl)
  const total = cart?.items?.length || 0
  const router = useRouter()
  const { lang } = router.query

  const trackSearchModalInteraction = useAnalyticsSearchModalInteraction(true)
  const userSignedOut = useAnalyticsUserAction('Signed Out')

  // Auth
  const { authenticated, unauthenticated, loading, user } = useAuthSession()

  const destinations = defaultPropertiesContext?.topbarConfiguration?.destinations
  const categories = defaultPropertiesContext?.topbarConfiguration?.categoryTypes
  const displayableLinks = defaultPropertiesContext?.topbarConfiguration?.displayableLinks
  const helpfulLinks = defaultPropertiesContext?.topbarConfiguration?.helpfulLinks
  const happyHour = defaultPropertiesContext?.happyHourEvent

  const [isOffCanvasMenuOpen, setOffCanvasMenuOpen] = useState(false)
  const [offCanvasSubmenu, setOffCanvasSubmenu] = useState('')
  const [offCanvasThirdmenu, setOffCanvasThirdMenu] = useState('')

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const firstInitial = firstName && firstName.length > 0 ? firstName.charAt(0) : ''
    const lastInitial = lastName && lastName.length > 0 ? lastName.charAt(0) : ''
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  const handleSearchModalOpen = () => {
    handleCloseOffCanvasMenu()
    setIsSearchModalOpen(true)
    trackSearchModalInteraction({ toggle: 'on', placement, type: searchType })
  }

  const handleSearchModalClose = (performedSearch = false) => {
    setIsSearchModalOpen(false)

    if (!performedSearch) {
      trackSearchModalInteraction({ toggle: 'off', placement, type: searchType })
    }
  }

  const handleSignOut = () => {
    signOut().then(() => {
      // Remove the session expiry date before logging event
      window.localStorage.removeItem(CACHE_EXPIRY_DATE_KEY_NAME)
      userSignedOut()
    })
  }

  const renderUser = () => {
    if (loading) return null

    if (unauthenticated) {
      return (
        <MenuLink
          href="#"
          onClick={() => {
            signIn('virgin')
          }}
        >
          Log in
        </MenuLink>
      )
    }

    if (authenticated) {
      const velocityButtonStyle = {
        background: '#E10A0A',
        borderRadius: '4px',
        borderColor: '#D6083B',
        color: 'white',
      }

      const velocityWrapper = (
        <>
          <VelocityIcon>
            <Icon name="velocityPoints" height={30} width={30} />
          </VelocityIcon>
          <Points>Points Balance</Points>
          <PointsWrapper>
            <PointValue>{user?.pointBalance?.toLocaleString()}</PointValue>
            <Icon name="chevronRightIcon" style={{ width: '16px', height: '16px' }} />
          </PointsWrapper>
        </>
      )

      const userDetailsWrapper = (
        <DropdownUserWrapper>
          <UserName>Hi {user?.firstName}</UserName>
          <Email>{user?.email}</Email>
          <Button onClick={handleSignOut} style={velocityButtonStyle}>
            Sign Out
          </Button>
        </DropdownUserWrapper>
      )

      return <Dropdown menuProp={velocityWrapper}>{userDetailsWrapper}</Dropdown>
    }
  }

  const handleBurgerMenu = () => {
    if (isOffCanvasMenuOpen) {
      setOffCanvasMenuOpen(false)
      setOffCanvasSubmenu('')
      setOffCanvasThirdMenu('')
      return
    }
    setOffCanvasMenuOpen(true)
  }

  const handleOpenOffCanvasMenu = (submenu?: string) => {
    setOffCanvasMenuOpen(true)
    if (submenu) {
      setOffCanvasSubmenu(submenu)
    }
  }

  const handleCloseOffCanvasMenu = () => {
    setOffCanvasMenuOpen(false)
    setOffCanvasSubmenu('')
    setOffCanvasThirdMenu('')
  }

  const renderMainHeaderLinks = () => {
    if (!displayableLinks) return

    return (
      <>
        {displayableLinks.map((item, index) => {
          if (item.subLinks) {
            return (
              <MenuItemWrapper
                key={item.label}
                data-testid={`${item.label?.toLowerCase()}-topbar`}
                onClick={() => {
                  handleOpenOffCanvasMenu(item.label)
                  setOffCanvasThirdMenu('')
                }}
              >
                <MenuItemText>{item.label}</MenuItemText>
                <Icon name="chevronDown" width={16} height={17} />
              </MenuItemWrapper>
            )
          } else {
            return (
              <MenuItemWrapper key={item.label} onClick={() => (window.location.href = `${baseUrl}${item.url}`)}>
                {item.label}
              </MenuItemWrapper>
            )
          }
        })}
      </>
    )
  }

  const topbarRef = useRef<HTMLDivElement>(null)
  const [topbarHeight, setTopbarHeight] = useState(0)

  useEffect(() => {
    if (topbarRef.current !== null) {
      const topbarHeight = topbarRef.current.clientHeight
      setTopbarHeight(topbarHeight)
    }
  }, [topbarRef.current])

  return (
    <>
      <TopbarContainer ref={topbarRef} id="top-bar-wrapper">
        {happyHour && (
          <HappyHour
            content={happyHour.content}
            mobileContent={happyHour?.mobileContent}
            style={{ background: '#E10A0A', padding: '20px' }}
          />
        )}
        <ImageWrapper>
          <Link href="https://www.virginaustralia.com/au/en/#/" target="_blank">
            <DesktopLogo>
              <Image src={`${baseUrl}/logos/virgin-logo-dark.svg`} alt="Virgin Australia" width={136} height={40} />
            </DesktopLogo>
            <MobileLogo>
              <Image src={`${baseUrl}/logos/virgin-logo-dark.svg`} alt="Virgin Australia" width={100} height={30} />
            </MobileLogo>
          </Link>
          <Link href="https://www.velocityfrequentflyer.com/" target="_blank">
            <DesktopLogo>
              <Image
                src={`${baseUrl}/logos/velocity-logo-dark.svg`}
                alt="Velocity Frequent Flyer"
                width={116}
                height={40}
              />
            </DesktopLogo>
            <MobileLogo>
              <Image
                src={`${baseUrl}/logos/velocity-logo-dark.svg`}
                alt="Velocity Frequent Flyer"
                width={100}
                height={30}
              />
            </MobileLogo>
          </Link>
        </ImageWrapper>
        <Wrapper>
          <SearchModal
            initialActiveInput="location"
            isOpen={isSearchModalOpen}
            type={searchType}
            placement={placement}
            onClose={handleSearchModalClose}
            onSearch={trackSearchModalInteraction}
            destinations={destinations!}
            part1Type=""
            part2Type=""
            part1=""
            part2=""
            part3=""
          />
          <OffCanvasMenu
            topbarHeight={topbarHeight}
            triggerLogin={() => router.push(`/${router.query?.lang ?? 'en'}/login`)}
            isOpen={isOffCanvasMenuOpen}
            submenuToggled={offCanvasSubmenu}
            thirdMenuToggled={offCanvasThirdmenu}
            onClose={handleCloseOffCanvasMenu}
            onMenuChange={setOffCanvasSubmenu}
            onThirdMenuChange={setOffCanvasThirdMenu}
            destinations={destinations}
            categories={categories}
            displayableLinks={displayableLinks}
            helpfulLinks={helpfulLinks}
          />
          <MenuContainer>
            <Burger data-testid="burger-menu" isToggled={isOffCanvasMenuOpen} onClick={handleBurgerMenu}>
              <BurgerLine className="line-1" />
              <BurgerLine className="line-2" />
              <BurgerLine className="line-3" />
            </Burger>
            <Link href={`${baseUrl}/${lang}`}>
              <DesktopLogo>
                <Image src={`${baseUrl}/logos/exoz-logo.svg`} alt="Experience Oz" width={137} height={32} />
              </DesktopLogo>
              <MobileLogo>
                <Image src={`${baseUrl}/logos/exoz-logo-short.svg`} alt="Experience Oz" width={55} height={24} />
              </MobileLogo>
            </Link>
            <MenuItemWrapper
              data-testid={`destinations-topbar`}
              onClick={() => {
                handleOpenOffCanvasMenu('Destinations')
                setOffCanvasThirdMenu('')
              }}
            >
              <MenuItemText>Destinations</MenuItemText>
              <Icon name="chevronDown" width={16} height={17} />
            </MenuItemWrapper>
            <MenuItemWrapper
              data-testid={`experiences-topbar`}
              onClick={() => {
                handleOpenOffCanvasMenu('Experiences')
                setOffCanvasThirdMenu('')
              }}
            >
              <MenuItemText>Experiences</MenuItemText>
              <Icon name="chevronDown" width={16} height={17} />
            </MenuItemWrapper>
            {renderMainHeaderLinks()}
          </MenuContainer>
          <SearchContainer>
            <Icons>
              {unauthenticated && (
                <MenuLink
                  href="https://join.velocityfrequentflyer.com/?icmpid=va:vff:va_exp:joinvff:ao:na"
                  target="_blank"
                >
                  Join Velocity
                </MenuLink>
              )}
              {renderUser()}
              {/* search */}
              <SearchPopup
                baseUrl={baseUrl}
                push={router.push}
                lang={`${lang || 'en'}`}
                onSearch={async (param) => {
                  const { query, limit } = param
                  const res = await (
                    await fetch(`${baseUrl}/api/search`, {
                      method: 'POST',
                      body: JSON.stringify({ query, limit, lang }),
                    })
                  ).json()
                  return res
                }}
                popularUrl={`/things-to-do/hot-deals`}
                onProductRender={(product) => (
                  <ProductTile
                    id={product.id}
                    title={product.name}
                    supplier={product.operatorName}
                    price={product.retailPrice}
                    fromPrice={product.fromPrice}
                    region={product.primaryRegion}
                    canonicalRegionUrlSegment={product.canonicalRegionUrlSegment}
                    image={product.images?.[0] || ''}
                    url={product.url}
                    onClick={() => {}}
                    rating={product.rating}
                  />
                )}
              />
              {/* cart */}
              <Link href={`${baseUrl}/en/order/checkout`}>
                <CartIcon>
                  {total > 0 && <Total>{total}</Total>}
                  <Icon name="shoppingBag" />
                </CartIcon>
              </Link>
            </Icons>
          </SearchContainer>
        </Wrapper>
      </TopbarContainer>
      <BodySpacer height={topbarHeight} />
    </>
  )
}

export default VirginTopbar
